import { Skeleton } from 'antd'
import React from 'react'
import styled from 'styled-components'

import { cardBorder } from 'assets/styles/variables'
import { IssueCard } from 'features/trend-detection/components/IssueCard'
import { BenchmarkingInsightCard } from 'features/trend-detection/features/issue-feed/components/BenchmarkingInsightCard'
import { EmptyHint } from 'features/trend-detection/features/issue-feed/feed-styles'
import { ISSUE_DETECTION_AGENT_TYPE } from 'features/trend-detection/mixpanel'
import type {
  BenchmarkingInsightFeedItem,
  Chart,
  IssueCardData,
  KeyInsightItem,
} from 'features/trend-detection/types'
import { getEmergenceLevel } from 'features/trend-detection/utils/helpers'

interface KeyInsightsSectionProps {
  items: KeyInsightItem[]
  loading: boolean
  projectId: string | null
  isShared: boolean
  onIssueTrackingChange?: (issueUuid: string, nextTracked: boolean) => void
  trackingIssueUuid: string | null
  onBenchmarkingInsightClick: (insight: BenchmarkingInsightFeedItem) => void
  onAgentNameNavigate?: (ctx: { agentUuid: string; agentName: string }) => void
  /** Per-issue timeseries charts, keyed by issue uuid. */
  chartDataMap?: Record<string, Chart>
  /** Per-issue chart load state, keyed by issue uuid. */
  chartLoadingMap?: Record<string, boolean>
  /** Per-run project-total charts, keyed by run uuid. */
  projectTotalChartMap?: Record<string, Chart>
}

export const KeyInsightsSection: React.FC<KeyInsightsSectionProps> = ({
  items,
  loading,
  projectId,
  isShared,
  onIssueTrackingChange,
  trackingIssueUuid,
  onBenchmarkingInsightClick,
  onAgentNameNavigate,
  chartDataMap = {},
  chartLoadingMap = {},
  projectTotalChartMap = {},
}) => {
  if (loading) {
    return (
      <SkeletonStack>
        {Array.from({ length: 3 }).map((_, i) => (
          <SkeletonCard key={i}>
            <Skeleton active paragraph={{ rows: 3 }} />
          </SkeletonCard>
        ))}
      </SkeletonStack>
    )
  }

  if (items.length === 0) {
    return (
      <EmptyHint data-chameleon="key-insights-empty-state">
        No key insights this week — your agents are monitoring.
      </EmptyHint>
    )
  }

  return (
    <FeedList>
      {items.map((item, idx) =>
        item.type === 'issue' ? (
          renderIssueItem(item, idx, {
            projectId,
            isShared,
            onIssueTrackingChange,
            trackingIssueUuid,
            onAgentNameNavigate,
            chartDataMap,
            chartLoadingMap,
            projectTotalChartMap,
          })
        ) : (
          <BenchmarkingInsightCard
            key={`benchmarking:${item.uuid}`}
            insight={item}
            onClick={onBenchmarkingInsightClick}
            projectId={projectId}
          />
        ),
      )}
    </FeedList>
  )
}

interface IssueRenderContext {
  projectId: string | null
  isShared: boolean
  onIssueTrackingChange?: (issueUuid: string, nextTracked: boolean) => void
  trackingIssueUuid: string | null
  onAgentNameNavigate?: (ctx: { agentUuid: string; agentName: string }) => void
  chartDataMap: Record<string, Chart>
  chartLoadingMap: Record<string, boolean>
  projectTotalChartMap: Record<string, Chart>
}

function renderIssueItem(
  item: Extract<KeyInsightItem, { type: 'issue' }>,
  idx: number,
  ctx: IssueRenderContext,
): React.ReactElement {
  const issueCard: IssueCardData = {
    ...item.issue.issue_data,
    issue_uuid: item.issue.uuid,
  }
  return (
    <CardStack key={`issue:${item.run_uuid}:${item.issue.uuid}`}>
      <IssueCard
        issueCard={issueCard}
        issue={item.issue}
        issueIndex={idx}
        emergenceLevel={getEmergenceLevel(
          item.issue.issue_data.emergence_score_normalized,
        )}
        timeseriesChart={ctx.chartDataMap[item.issue.uuid]}
        chartLoading={ctx.chartLoadingMap[item.issue.uuid] ?? false}
        projectTotalChart={ctx.projectTotalChartMap[item.run_uuid] ?? null}
        agentUuid={item.agent_uuid}
        agentName={item.agent_name}
        agentType={ISSUE_DETECTION_AGENT_TYPE}
        runUuid={item.run_uuid}
        feedTab="feed"
        source="key_insights"
        onAgentNameNavigate={
          ctx.onAgentNameNavigate
            ? () =>
                ctx.onAgentNameNavigate?.({
                  agentUuid: item.agent_uuid,
                  agentName: item.agent_name,
                })
            : undefined
        }
        isShared={ctx.isShared}
        projectId={ctx.projectId}
        onIssueTrackingChange={
          ctx.onIssueTrackingChange
            ? (next) => ctx.onIssueTrackingChange?.(item.issue.uuid, next)
            : undefined
        }
        trackingLoading={ctx.trackingIssueUuid === item.issue.uuid}
      />
    </CardStack>
  )
}

const FeedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
`

const CardStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`

const SkeletonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
`

const SkeletonCard = styled.div`
  background: white;
  border: 1px solid ${cardBorder};
  border-radius: 8px;
  padding: 20px;
`
